'use client'

import {
  useRef,
  useState,
  useLayoutEffect,
  useCallback,
  type ReactNode,
  type CSSProperties,
} from 'react'
import { AnimatePresence, motion } from 'motion/react'
import { cn, delayedHover } from '@/lib/utils'
import Portal from './Portal'

type OverviewProps = OverviewContainerProps & {
  children: ReactNode
  className?: string
  onVisibilityChange?: (isVisible: boolean) => void
}

type OverviewContainerProps = {
  content: ReactNode
}

type OverviewPositionProps = {
  position: { top: number; left: number; gap: CSSProperties }
}

type OverviewAlignmentProps = {
  horizontalAlign?: 'left' | 'center' | 'right'
  verticalAlign?: 'top' | 'center' | 'bottom'
  horizontalGap?: number
  verticalGap?: number
}

export default function Overview({
  children,
  content,
  horizontalAlign = 'center',
  verticalAlign = 'top',
  horizontalGap = 8,
  verticalGap = 8,
  className,
  onVisibilityChange,
}: OverviewProps & OverviewAlignmentProps) {
  const triggerRef = useRef<HTMLDivElement>(null)
  const [showOverview, setShowOverview] = useState(false)
  const [position, setPosition] = useState<{
    top: number
    left: number
    gap: CSSProperties
  } | null>(null)

  const updatePosition = useCallback(() => {
    if (!triggerRef.current) return
    const rect = triggerRef.current.getBoundingClientRect()

    let top: number
    let left: number
    let gap: CSSProperties = {}
    let offsetHeight: number

    // Horizontal positioning
    if (horizontalAlign === 'right') {
      gap = { paddingLeft: horizontalGap }
      left = rect.left + rect.width + window.scrollX
    } else if (horizontalAlign === 'left') {
      gap = { paddingRight: horizontalGap }
      left = rect.right - rect.width + window.scrollX
    } else {
      left = rect.left + rect.width / 2 + window.scrollX
    }

    // Vertical positioning
    if (verticalAlign === 'center') {
      top = rect.top + rect.height / 2 + window.scrollY
    } else if (verticalAlign === 'bottom') {
      gap = { ...gap, paddingBottom: horizontalAlign === 'center' ? verticalGap : 0 }
      offsetHeight = horizontalAlign === 'center' ? rect.height : 0
      top = rect.bottom - offsetHeight + window.scrollY
    } else {
      gap = { ...gap, paddingTop: horizontalAlign === 'center' ? verticalGap : 0 }
      offsetHeight = horizontalAlign === 'center' ? rect.height : 0
      top = rect.top + offsetHeight + window.scrollY
    }

    setPosition({ top, left, gap })
  }, [horizontalAlign, verticalAlign, horizontalGap, verticalGap])

  const delayOverview = delayedHover((value: boolean) => {
    setShowOverview(value)
    onVisibilityChange?.(value)
    if (value) updatePosition()
  }, true)

  // optional: keep aligned while scrolling containers move
  useLayoutEffect(() => {
    if (!showOverview) return

    window.addEventListener('scroll', updatePosition, true)
    window.addEventListener('resize', updatePosition)

    return () => {
      window.removeEventListener('scroll', updatePosition, true)
      window.removeEventListener('resize', updatePosition)
    }
  }, [showOverview, updatePosition])

  return (
    <div ref={triggerRef} {...delayOverview} className={cn('w-fit', className)}>
      {children}

      <Portal>
        <AnimatePresence>
          {showOverview && position && (
            <OverviewContainer
              position={position}
              content={content}
              horizontalAlign={horizontalAlign}
              verticalAlign={verticalAlign}
            />
          )}
        </AnimatePresence>
      </Portal>
    </div>
  )
}

export function OverviewContainer({
  position,
  content,
  horizontalAlign = 'center',
  verticalAlign = 'top',
}: OverviewContainerProps & OverviewPositionProps & OverviewAlignmentProps) {
  if (!content) return null

  const xPosition =
    horizontalAlign === 'center' ? '-50%' : horizontalAlign === 'left' ? '-100%' : '0%'
  const yPosition =
    verticalAlign === 'center' ? '-50%' : verticalAlign === 'bottom' ? '-100%' : '0%'

  return (
    <motion.div
      initial={{ opacity: 0, y: `calc(${yPosition} + 10px)`, x: xPosition }}
      animate={{ opacity: 1, y: yPosition, x: xPosition }}
      exit={{ opacity: 0, y: `calc(${yPosition} + 10px)`, x: xPosition, pointerEvents: 'none' }}
      className="absolute z-[9999]"
      style={{ top: position.top, left: position.left, ...position.gap }}
    >
      {content}
    </motion.div>
  )
}
